<template>
    <div class="RPDropdown">
        <FieldLabel
            :label="label"
            :label-tooltip="labelTooltip"
            :disabled="disabled"
            :optional="optional"
            :status="status"
        />
        <VuelidateWrapper
            :name="name"
            :value="value"
            :status="status"
        >
            <template v-slot:default="{ internalStatus, statusMessage }">
                <div
                    :style="{ width: widthFormat }"
                    class="RPDropdown__container"
                >
                    <div
                        :class="buttonClass(internalStatus)"
                        role="button"
                        @click="toggleDropdown"
                    >
                        <template v-if="!isLoading">
                            <RPTooltip
                                :status="internalStatus"
                                :tooltip="statusMessage"
                                class="RPDropdown__tooltip"
                            />
                            <span
                                v-if="showPlaceholder"
                                class="RPDropdown__placeholder"
                            >
                                {{ placeholder }}
                            </span>

                            <template v-else>
                                <div
                                    v-if="multiple"
                                    class="RPDropdown__tags-container"
                                >
                                    <template v-for="tag in validationValue">
                                        <Tag
                                            :key="tag.value"
                                            :value="selectedOption(tag)"
                                            @deselect="deselectOption"
                                        />
                                    </template>
                                </div>
                                <div
                                    v-else
                                    class="RPDropdown__single"
                                >
                                    <StreamlineIcon
                                        v-if="!isEmpty(selectedOption(validationValue).icon)"
                                        class="RPDropdown__single-icon"
                                        :icon="selectedOption(validationValue).icon"
                                        :size="16"
                                        :stroke="selectedOption(validationValue).color"
                                    />
                                    {{ selectedOption(validationValue).label }}
                                </div>
                            </template>
                            <div class="RPDropdown__arrow">
                                <StreamlineIcon
                                    :fill="arrowColor"
                                    :icon="arrowIcon"
                                    :size="12"
                                    stroke="transparent"
                                />
                            </div>
                        </template>
                    </div>
                    <div
                        v-if="showDropdown"
                        :class="[
                            'RPDropdown__options-container',
                            `RPDropdown__options-container--${dropdownPosition}`
                        ]"
                    >
                        <template v-for="option in optionsFormatted">
                            <!-- @slot Replace the individual options in the dropdown -->
                            <slot
                                :option="option"
                                name="optionSlot"
                            >
                                <DropdownOption
                                    :key="option.value"
                                    :value="option.value"
                                    :label="option.label"
                                    :icon="option.icon"
                                    :color="option.color"
                                    :description="option.description"
                                    :show-checkbox="multiple"
                                    :selected="isSelected(option.value)"
                                    @select="selectOption"
                                    @deselect="deselectOption"
                                />
                            </slot>
                        </template>
                    </div>
                </div>
            </template>
        </VuelidateWrapper>
    </div>
</template>
<script>
import DropdownOption from "./_dropdown-option.vue";
import Tag from "./_tag.vue";
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {ArrowUp1, ArrowDown1} from "@reviewpush/streamline-bold/lib/arrows-diagrams/Arrows";
import {cloneDeep,isEmpty} from 'lodash';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import colors from 'style/_colors.scss';
import VuelidateWrapper from '../VuelidateWrapper';
import RPTooltip from '../../Misc/RPTooltip.vue';
import has from 'lodash/has';
import FieldLabel from 'components/Basic/Misc/FieldLabel';

export default {
    name: 'RPDropdown',
    components: {
        FieldLabel,
        StreamlineIcon,
        DropdownOption,
        Tag,
        VuelidateWrapper,
        RPTooltip
    },
    props: {
        /**
         * A label to display above the dropdown
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Text to display in an informational tooltip next to the label.
         */
        labelTooltip: {
            type: String,
            default: '',
        },
        /**
         * @model
         */
        value: {
            required: true,
            // Workaround to allow null
            validator: prop => true
        },
        name: {
            type: String,
            default: '',
        },
        /**
         * An array of any type of values to be used as options.
         */
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: 'Select Option...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        width: {
            type: [String, Number],
            default: 'fit-content'
        },
        dropdownPosition: {
            type: String,
            default: 'right',
            validator: val => ['left', 'right', 'center'].indexOf(val) !== -1
        },
        optional: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, you cannot deselect the currently selected option.
         */
        required: {
            type: Boolean,
            default: false
        },
        /**
         * A status pulled from the common `status` constants (`/src/status/index.js`)
         */
        status: {
            type: String,
            default: stat.STANDBY
        }
    },
    data(){
        return {
            showDropdown: false,
        };
    },
    computed: {
        widthFormat(){
            return typeof this.width ==='string'
                ? this.width
                : `${this.width}rem`;
        },
        hasValidations(){
            return has(this.value, '$model');
        },
        validationValue(){
            return this.hasValidations
                ? this.value.$model
                : this.value;
        },
        isRequired(){
            return this.required
                || (this.hasValidations && has(this.value, 'required'));
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        multiple(){
            return !this.isPrimitive(this.validationValue);
        },
        optionsFormatted(){
            return this.options.map(opt => {
                return this.isPrimitive(opt)
                    ? {
                        value: opt,
                        label: String(opt),
                        description: '',
                        icon: {},
                        color: '',
                    }
                    : Object.assign({
                        value: '',
                        label: '',
                        description: '',
                        icon: {},
                        color: '',
                    }, opt);
            });
        },
        showPlaceholder(){
            return this.multiple
                ? this.validationValue.length < 1
                : !this.selectedOption(this.validationValue);
        },
        arrowIcon(){
            return this.showDropdown
                ? ArrowUp1
                : ArrowDown1;
        },
        arrowColor(){
            return (this.disabled)
                ? colors.grey100
                : colors.textGrey;
        },
    },
    watch: {
        showDropdown(newVal, oldVal){
            if(newVal !== oldVal && !newVal && this.hasValidations){
                this.value.$touch();
            }
        }
    },
    mounted(){
        document.addEventListener('click', this.closeDropdown);
    },
    destroyed(){
        document.removeEventListener('click', this.closeDropdown);
    },
    methods: {
        isEmpty,
        isPrimitive(val) {
            return typeof val !== 'object' || val === null;
        },
        closeDropdown(e){
            if(!this.$el.contains(e.target)){
                this.showDropdown = false;
            }
        },
        buttonClass(internalStatus){
            return statusBuilder(
                'RPDropdown__button',
                internalStatus,
                this.disabled,
                {
                    opened: this.showDropdown
                }
            );
        },
        selectedOption(val){
            return this.optionsFormatted.find(o => o.value === val);
        },
        toggleDropdown(){
            if(!this.disabled){
                this.showDropdown = !this.showDropdown;
            }
        },
        isSelected(opt){
            return this.multiple
                ? this.validationValue.findIndex(v => v === opt) !== -1
                : this.validationValue === opt;
        },
        input(val){
            if(this.hasValidations) {
                this.value.$model = val;
            } else {
                this.$emit('input', val);
            }
        },
        selectOption(val){
            let v = val;
            if(this.multiple){
                v = cloneDeep(this.validationValue);
                v.push(val);
            } else {
                this.showDropdown = false;
            }
            this.input(v);
        },
        deselectOption(val){
            if(this.multiple){
                this.input(cloneDeep(this.validationValue).filter(v => v !== val));
            }
            if(!this.multiple && !this.isRequired){
                this.input(null);
                this.showDropdown = false;
            }
        },
    }
};
</script>
<style lang="scss">
.RPDropdown {
    position: relative;
    &__container{
        position: relative;
    }
    &__tooltip {
        margin-right: 0.25rem;
    }
    &__placeholder {
        white-space: nowrap;
        margin: auto 0;
        color: $text-grey;
    }
    &__single {
        display: flex;
        align-items: center;
        color: $text-dark;

        &-icon {
            margin-right: 0.25rem
        }
    }
    &__tags-container{
        padding: .25rem 0 0 0;
        display: flex;
        flex-wrap: wrap;
    }
    &__button {
        cursor: pointer;
        min-height: 2rem;
        height: fit-content;
        border: 1px solid $grey-100;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 .5rem;
        &:focus{
            outline: none;
            border-color: $primary;
        }

        @include loading(2rem, 13rem);

        &--success {
            border-color: $success-200;
            background: $success-50;
        }
        &--error {
            border-color: $error-200;
            background: $error-50;
        }
        &--warning {
            border-color: $warning-200;
            background: $warning-50;
        }

        &--opened {
            border-bottom: 1px solid transparent;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &--disabled {
            color: $grey-100;
            background: $grey-50;
        }
    }
    &__arrow {
        margin-left: 0.25rem;
        height: 1rem;
    }
    &__options-container {
        position:absolute;
        min-width: 100%;
        z-index: 1000;
        border-left: 1px solid $grey-100;
        border-right: 1px solid $grey-100;
        border-top: 1px solid $grey-100;
        box-sizing: border-box;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        background: $white;
        &--right {
            right: 0;
        }
        &--left {
            left: 0;
        }
        &--center {
            left: 50%;
            transform: translateX(-50%);
        }
    }
}
</style>

<docs>
Options can contain an array of primitives. When you deselect an item, the value of `v-model` is set to `null` (unless the `required` prop is true).

```vue
<RPDropdown
    v-model="myVal"
    label="Example Label"
    label-tooltip="Example Tooltip"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<pre>{{ {"v-model": myVal} }}</pre>
<RPDropdown
    v-model="myVal"
    label="Example Label"
    label-tooltip="Example Tooltip"
    dropdownPosition="left"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<RPDropdown
    v-model="myVal"
    label="Example Label"
    label-tooltip="Example Tooltip"
    dropdownPosition="center"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
```

Pass an array to `v-model` to create a multiselect. When no items are selected, the value of `v-model` will be an empty array rather than `null`.
```vue

<RPDropdown
    v-model="myValArray"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<pre>{{ {"v-model": myValArray} }}</pre>
```

The `options` array can also be a list of objects with these properties:

```html static
:options="[
{value: 'apple', label: 'Apple', description: 'Crunchy and tart', icon: FruitApple},
{value: 'banana', label: 'Banana', description: 'Soft and sweet', icon: Fruits.FruitBanana}
]"
```

```vue
import { FruitApple, FruitBanana } from "@reviewpush/streamline-regular/lib/food-drinks/Fruits";
<RPDropdown
    v-model="myValArray"
    placeholder="Pick a fruit:"
    :options="[
        {value: 'apple', label: 'Apple', description: 'Crunchy and tart', icon: FruitApple},
        {value: 'banana', label: 'Banana', description: 'Soft and sweet', icon: FruitBanana}
    ]"
/>
<pre>{{ {"v-model": myValArray} }}</pre>
<RPDropdown
    v-model="myVal"
    placeholder="Pick a fruit:"
    :options="[
        {value: 'apple', label: 'Apple', icon: FruitApple, color: 'red'},
        {value: 'banana', label: 'Banana', icon: FruitBanana, color: 'yellow'}
    ]"
/>
<pre>{{ {"v-model": myVal} }}</pre>
```

```vue
<RPDropdown
    v-model="myValArray"
    disabled
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<br>
<RPDropdown
    v-model="myValArray"
    status="success"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<br>
<RPDropdown
    v-model="myValArray"
    status="error"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
```
Validation Example
```[import](./RPDropdownExample.vue)
```
</docs>
